import React, { createContext, useState, useContext, useRef, ReactNode, FC, useMemo } from 'react';

import { Button, makeStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

// eslint-disable-next-line import/no-cycle
import DialogGrid from 'components/_dialogs/_components/DialogGrid';
import ColoredButton from 'components/ColoredButton';
import general_messages from 'messages/general_messages';

const useStyles = makeStyles(theme => ({
  button: {
    // @ts-ignore
    minWidth: theme.sizes.button.narrow,
    width: '100%',
  },
  wrapper: {
    display: 'flex',
    gap: theme.spacing(3),
    width: '100%',
  },
}));

type ConfirmationModalContextType = {
  showConfirmationModal: (options: ModalOptions) => Promise<boolean>;
  showUnsavedChangesModal: (options: UnsavedChangesModalOptions) => Promise<'save' | 'discard' | 'cancel'>;
  closeConfirmationModal: () => void;
};

const ConfirmationModalContext = createContext({} as ConfirmationModalContextType);

const { Provider } = ConfirmationModalContext;

type Props = {
  children: ReactNode;
};

type ModalOptions = {
  title: ReactNode;
  body: ReactNode;
  yesLabel?: ReactNode;
  noLabel?: ReactNode;
  hideYesButton?: boolean;
};

type UnsavedChangesModalOptions = {
  title?: ReactNode;
  body?: ReactNode;
  saveAndLeaveLabel?: ReactNode;
  leaveWithoutSavingLabel?: ReactNode;
  cancelLabel?: ReactNode;
};

const ConfirmationModalProvider: FC<Props> = ({ children }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [confirmationOptions, setConfirmationOptions] = useState<ModalOptions | null>(null);
  const [unsavedChangesOptions, setUnsavedChangesOptions] = useState<UnsavedChangesModalOptions | null>(null);
  const [modalType, setModalType] = useState<'confirm' | 'unsaved-changes'>('confirm');

  const awaitingConfirmation: React.MutableRefObject<{ resolve: (result: boolean) => void } | undefined> = useRef(undefined);
  const awaitingUnsavedChangesConfirmation: React.MutableRefObject<
    { resolve: (result: 'save' | 'discard' | 'cancel') => void } | undefined
  > = useRef(undefined);
  const closeModalTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const closeModal = () => {
    if (closeModalTimeoutRef.current) {
      clearTimeout(closeModalTimeoutRef.current);
      closeModalTimeoutRef.current = null;
    }
    setOpen(false);
    closeModalTimeoutRef.current = setTimeout(() => {
      closeModalTimeoutRef.current = null;
      setConfirmationOptions(null);
      setUnsavedChangesOptions(null);
      setModalType('confirm');
    }, 300);
  };

  const closeModalWithResolve = (resolve?: boolean) => {
    awaitingConfirmation.current?.resolve(!!resolve);
    closeModal();
  };

  const showConfirmationModal = (options: ModalOptions) => {
    if (closeModalTimeoutRef.current) {
      clearTimeout(closeModalTimeoutRef.current);
      closeModalTimeoutRef.current = null;
    }
    setModalType('confirm');
    setUnsavedChangesOptions(null);
    setOpen(true);
    setConfirmationOptions(options);
    return new Promise<boolean>(resolve => {
      awaitingConfirmation.current = { resolve };
    });
  };

  const showUnsavedChangesModal = (options: UnsavedChangesModalOptions = {}) => {
    if (closeModalTimeoutRef.current) {
      clearTimeout(closeModalTimeoutRef.current);
      closeModalTimeoutRef.current = null;
    }
    setModalType('unsaved-changes');
    setConfirmationOptions(null);
    setOpen(true);
    setUnsavedChangesOptions(options);
    return new Promise<'save' | 'discard' | 'cancel'>(resolve => {
      awaitingUnsavedChangesConfirmation.current = { resolve };
    });
  };

  const onCancel = () => {
    if (modalType === 'unsaved-changes' && awaitingUnsavedChangesConfirmation.current) {
      awaitingUnsavedChangesConfirmation.current.resolve('cancel');
    } else if (awaitingConfirmation.current) {
      awaitingConfirmation.current.resolve(false);
    }

    closeModal();
  };

  const onConfirm = () => {
    if (awaitingConfirmation.current) {
      awaitingConfirmation.current.resolve(true);
    }

    closeModal();
  };

  const onSaveAndLeave = () => {
    if (awaitingUnsavedChangesConfirmation.current) {
      awaitingUnsavedChangesConfirmation.current.resolve('save');
    }
    closeModal();
  };

  const onLeaveWithoutSaving = () => {
    if (awaitingUnsavedChangesConfirmation.current) {
      awaitingUnsavedChangesConfirmation.current.resolve('discard');
    }
    closeModal();
  };

  const value = useMemo(() => ({ showConfirmationModal, showUnsavedChangesModal, closeConfirmationModal: closeModalWithResolve }), []);

  const styles = useStyles();
  return (
    <Provider value={value as ConfirmationModalContextType}>
      {children}
      {/* @ts-ignore */}
      <DialogGrid
        dialogActions={
          modalType === 'unsaved-changes' ? (
            <div className={styles.wrapper}>
              <Button className={styles.button} onClick={onCancel} size='small' variant='outlined'>
                {unsavedChangesOptions?.cancelLabel || t(general_messages.cancel)}
              </Button>
              {/* @ts-ignore */}
              <ColoredButton className={styles.button} customColor='warning' onClick={onLeaveWithoutSaving} size='small' variant='outlined'>
                {unsavedChangesOptions?.leaveWithoutSavingLabel || t(general_messages.unsaved_changes_leave_without_saving)}
              </ColoredButton>
              {/* @ts-ignore */}
              <ColoredButton className={styles.button} customColor='secondary' onClick={onSaveAndLeave} size='small' variant='outlined'>
                {unsavedChangesOptions?.saveAndLeaveLabel || t(general_messages.unsaved_changes_save_and_leave)}
              </ColoredButton>
            </div>
          ) : (
            <div className={styles.wrapper}>
              <Button className={styles.button} onClick={onCancel} variant='outlined'>
                {confirmationOptions?.noLabel || t(general_messages.no)}
              </Button>
              {confirmationOptions?.hideYesButton ? null : (
                // @ts-ignore
                <ColoredButton className={styles.button} customColor='secondary' onClick={onConfirm} variant='outlined'>
                  {confirmationOptions?.yesLabel || t(general_messages.yes)}
                </ColoredButton>
              )}
            </div>
          )
        }
        onClose={onCancel}
        open={open}
        title={
          modalType === 'unsaved-changes'
            ? unsavedChangesOptions?.title || t(general_messages.unsaved_changes_title)
            : confirmationOptions?.title || null
        }
      >
        {modalType === 'unsaved-changes'
          ? unsavedChangesOptions?.body || t(general_messages.unsaved_changes_body)
          : confirmationOptions?.body || null}
      </DialogGrid>
    </Provider>
  );
};

export const useConfirmationModalContext = () => useContext(ConfirmationModalContext);

export default ConfirmationModalProvider;
